import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Label from 'LuxKit/Label/Label'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineArrowsRightDownIcon from 'LuxKit/Icons/line/LineArrowsRightDownIcon'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { PropertyAnytimeRateOptionContent } from 'components/OfferPage/Property/PropertyAnytimeRateOption'
import { getRoomsMappings } from 'components/OfferPage/Property/singlePropertyUtils'
import AmenityItem from './AmenityItem'
import { ROOM_CARD_HORIZONTAL_BREAKPOINT } from './constants'
import type { DrawerHotelRoomOption } from './types'

const RoomCard = styled.div`
  container-type: inline-size;
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.S};
  overflow: hidden;
`

/**
 * Stacked by default — the drawer is regularly narrower than the viewport (mobile, and
 * the resizable right panel), and the image column alone is wider than a mobile drawer.
 * The image/details columns come back at the same width the other drawer room cards use.
 */
const RoomContent = styled.div`
  display: flex;
  flex-direction: column;

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    flex-direction: row;
  }
`

const RoomImageWrapper = styled.div`
  width: 100%;
  /* Stacked, the image needs its own definite height to resolve ImageCarousel's
     internal height: 100%. Matches the image row of DrawerAccommodationRoomTile. */
  height: ${rem(210)};
  flex-shrink: 0;
  overflow: hidden;

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    width: ${rem(280)};
    max-width: ${rem(350)};
    /* Stretch to match the right column's natural height so the image fills
       the full card. align-self: stretch gives the wrapper a definite cross-size
       from flex layout, which resolves ImageCarousel's internal height: 100%.
       min-height acts as a floor when the content column is unusually short. */
    height: auto;
    align-self: stretch;
    min-height: ${rem(200)};
  }
`

const RoomDetails = styled.div`
  flex: 1;
  min-width: 0;
  padding: ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    padding: ${rem(20)};
  }
`

const AmenitiesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)} ${rem(16)};
`

const RoomFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  padding: ${rem(16)} ${rem(12)};
  border-top: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  min-width: ${rem(80)};
`

const PackagesLink = styled(RoomFooter).attrs({ as: 'a' })`
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  color: ${(props) => props.theme.palette.brand.primary.normal};
  font-weight: ${(props) => props.theme.font.secondary.weight.superBold};
  font-size: ${rem(14)};
  line-height: ${rem(20)};

  &:hover {
    text-decoration: underline;
  }
`

const MAX_AMENITIES = 6

interface ExternalPackagesFooterProps {
  room: DrawerHotelRoomOption
  deeplinkUrl?: string
}

function ExternalPackagesFooter({ room, deeplinkUrl }: ExternalPackagesFooterProps) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const geo = useContext(GeoContext)

  const packagesUrl = useMemo(() => {
    if (!deeplinkUrl) return undefined
    return addQueryParamsToPath(deeplinkUrl, { roomTypeId: room.roomId })
  }, [deeplinkUrl, room.roomId])

  if (!packagesUrl) return null

  const formattedLowestPrice = room.lowestPrice !== undefined
    ? formatRoundedAmountWithDollarType(room.lowestPrice, room.currencyCode ?? currentCurrency, geo.currentRegionCode)
    : '—'

  const packagesLinkText = room.packageCount > 0
    ? intl.formatMessage(
        {
          id: 'exNWvzzbAW-ImBkaeMSy',
          defaultMessage: 'Show {count, plural, one {# package} other {# packages}} from {price}',
          description: 'Link text showing number of available packages and starting price for a room in the hotel drawer',
        },
        { count: room.packageCount, price: formattedLowestPrice },
      )
    : intl.formatMessage({
        id: 'y185Krcwi0DA71rua3rG',
        defaultMessage: 'View packages',
        description: 'Link text to view available packages on the offer page when no pricing is available in the hotel drawer',
      })

  return (
    <PackagesLink href={packagesUrl} target="_blank" rel="noopener noreferrer">
      {packagesLinkText}
      <LineExternalLinkAltIcon size="S" />
    </PackagesLink>
  )
}

interface ExternalRoomCardProps {
  kind: 'external'
  deeplinkUrl?: string
}

interface PropertyRoomCardProps {
  kind: 'property'
  property: App.Property.Property
  propertyRoomsBySourceId: Record<string, App.Property.Room>
  duration: number
  onSelectDates: (roomId: string) => void
}

type RoomCardMode = ExternalRoomCardProps | PropertyRoomCardProps

interface RoomCardItemProps {
  room: DrawerHotelRoomOption
  mode: RoomCardMode
}

function RoomCardItem({ room, mode }: RoomCardItemProps) {
  const intl = useIntl()
  const propertyRoom = mode.kind === 'property' ? mode.propertyRoomsBySourceId[room.roomId] : undefined
  const maximumCapacity = Math.max(0, ...room.capacities.map(capacity => capacity.adults + capacity.children))
  const roomSize = room.sizeSqm ? `${room.sizeSqm}m²` : undefined

  const visibleAmenities = room.amenities.slice(0, MAX_AMENITIES)
  const urgencyLabels = room.urgencyTags.map((tag) => (
    <MessagingLabel key={tag.type} variant="urgency">
      {tag.label}
    </MessagingLabel>
  ))
  const carouselImages = useMemo<Array<App.Image>>(
    () => room.imageIds.map((id) => ({ id })),
    [room.imageIds],
  )
  const singleImageId = carouselImages.length === 1 ? carouselImages[0].id : undefined

  let roomImage: React.ReactNode = null
  if (singleImageId) {
    roomImage = <ResponsiveImage id={singleImageId} alt={room.name} fit="center" />
  } else if (carouselImages.length > 1) {
    roomImage = <ImageCarousel images={carouselImages} arrowBehaviour="interaction-visible" />
  }

  return (
    <RoomCard data-testid="drawer-room-card">
      <RoomContent data-testid="drawer-room-card-content">
        {roomImage && (
          <RoomImageWrapper>
            {roomImage}
          </RoomImageWrapper>
        )}
        <RoomDetails>
          <Heading variant="heading4">{room.name}</Heading>

          {propertyRoom ? (
            <AccommodationRoomDetailsList
              roomPackage={propertyRoom}
              urgencyLabels={urgencyLabels}
            />
          ) : (
            <>
              <LabelGroup>
                {urgencyLabels}
                {roomSize && (
                  <Label kind="tertiary" variant="ghost" startIcon={<LineArrowsRightDownIcon size="XS" />}>
                    {roomSize}
                  </Label>
                )}
                {maximumCapacity > 0 && (
                  <Label kind="tertiary" variant="ghost" startIcon={<LineUserIcon size="XS" />}>
                    {intl.formatMessage(
                      {
                        id: 'g31GXXkjeIsviY6IZREf',
                        defaultMessage: 'Sleeps {count}',
                        description: 'Label showing room capacity in hotel drawer room options',
                      },
                      { count: maximumCapacity },
                    )}
                  </Label>
                )}
                {room.bedGroupDescriptions.map((bed) => (
                  <Label key={bed} kind="tertiary" variant="ghost" startIcon={<LineBedIcon size="XS" />}>
                    {bed}
                  </Label>
                ))}
              </LabelGroup>

              {visibleAmenities.length > 0 && (
                <AmenitiesGrid>
                  {visibleAmenities.map((amenity) => (
                    <AmenityItem key={amenity.name} amenity={amenity} />
                  ))}
                </AmenitiesGrid>
              )}
            </>
          )}
        </RoomDetails>
      </RoomContent>

      {propertyRoom && mode.kind === 'property' && (
        <PropertyAnytimeRateOptionContent
          property={mode.property}
          propertyRoom={propertyRoom}
          anytimeRoomRate={undefined}
          duration={mode.duration}
        />
      )}

      {mode.kind === 'property' && (
        <RoomFooter>
          <BodyText variant="large" weight="bold">
            {intl.formatMessage({
              id: 'Q81wyQpQgKNK8UxtYyU6',
              defaultMessage: 'Select dates to unlock package options',
              description: 'Placeholder text when dates aren’t selected for property anytime rate option',
            })}
          </BodyText>
          <TextButton size="large" kind="primary" onClick={() => mode.onSelectDates(propertyRoom?.id ?? room.roomId)}>
            {intl.formatMessage({
              id: 'RkyCUVscJAILoveCQYfQ',
              defaultMessage: 'Select dates',
              description: 'CTA button text to prompt the user to select dates.',
            })}
          </TextButton>
        </RoomFooter>
      )}

      {mode.kind === 'external' && <ExternalPackagesFooter room={room} deeplinkUrl={mode.deeplinkUrl} />}
    </RoomCard>
  )
}

interface RoomOptionsListProps {
  roomOptions: Array<DrawerHotelRoomOption>
  showHeading: boolean
  mode: RoomCardMode
}

function RoomOptionsList({ roomOptions, showHeading, mode }: RoomOptionsListProps) {
  const intl = useIntl()

  if (roomOptions.length === 0) return null

  return (
    <Group direction="vertical" gap={12}>
      {showHeading && <Heading variant="heading5">
        {intl.formatMessage({
          id: '3BjVOvp7IcByyR1-eSdv',
          defaultMessage: 'Room options',
          description: 'Heading for the room options section in the hotel detail drawer',
        })}
      </Heading>}
      <Group direction="vertical" gap={32}>
        {roomOptions.map((room) => (
          <RoomCardItem
            key={room.roomId}
            room={room}
            mode={mode}
          />
        ))}
      </Group>
    </Group>
  )
}

interface HotelDetailRoomOptionsProps {
  roomOptions: Array<DrawerHotelRoomOption>
  deeplinkUrl?: string
}

function HotelDetailRoomOptions({ roomOptions, deeplinkUrl }: HotelDetailRoomOptionsProps) {
  return <RoomOptionsList
    roomOptions={roomOptions}
    showHeading
    mode={{ kind: 'external', deeplinkUrl }}
  />
}

interface PropertyDrawerRoomOptionsProps {
  roomOptions: Array<DrawerHotelRoomOption>
  property: App.Property.Property
  roomMappings?: App.PropertyOfferMapping['rooms']
  duration: number
  onSelectDates: (roomId: string) => void
}

export function PropertyDrawerRoomOptions({ roomOptions, property, roomMappings, duration, onSelectDates }: PropertyDrawerRoomOptionsProps) {
  const [, lpcRooms] = useMemo(
    () => getRoomsMappings(property.rooms, roomMappings),
    [property.rooms, roomMappings],
  )
  const propertyRoomsBySourceId = useMemo(
    () => ({ ...property.rooms, ...lpcRooms }),
    [lpcRooms, property.rooms],
  )

  return <RoomOptionsList
    roomOptions={roomOptions}
    showHeading={false}
    mode={{ kind: 'property', property, propertyRoomsBySourceId, duration, onSelectDates }}
  />
}

export default HotelDetailRoomOptions
